import React from "react";
import type { EdgeKind } from "@/data/architecture";

interface LegendEntry {
  kind: EdgeKind;
  label: string;
  meaning: string;
}

const LEGEND: LegendEntry[] = [
  {
    kind: "governs",
    label: "governs",
    meaning: "Foundation or governance binding the layer below",
  },
  {
    kind: "enforces",
    label: "enforces",
    meaning: "the Bill of Rights binding a schema field or surface mode",
  },
  {
    kind: "anchors-to",
    label: "anchors to",
    meaning: "a claim that gains its force from a chain anchor",
  },
  {
    kind: "stored-in",
    label: "stored in",
    meaning: "content-addressed storage (registry, IPFS)",
  },
];

/**
 * Strokes used here mirror the SVG path attributes in ProtocolMap so the
 * legend is the source of truth for the visual grammar.
 */
function strokeProps(kind: EdgeKind): {
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
} {
  switch (kind) {
    case "governs":
      return { stroke: "var(--color-ember)", strokeWidth: 1.2 };
    case "enforces":
      return { stroke: "var(--color-claude)", strokeWidth: 1.4 };
    case "anchors-to":
      return { stroke: "var(--color-ink)", strokeWidth: 1.2 };
    case "stored-in":
      return {
        stroke: "var(--color-ink-3)",
        strokeWidth: 1,
        strokeDasharray: "5 4",
      };
  }
}

export function ProtocolMapLegend() {
  return (
    <div className="border-t border-hairline pt-4 mt-4 flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {LEGEND.map((entry) => {
          const props = strokeProps(entry.kind);
          return (
            <div
              key={entry.kind}
              className="flex flex-col gap-1.5"
            >
              <div className="flex items-center gap-2">
                <svg
                  width="36"
                  height="10"
                  viewBox="0 0 36 10"
                  aria-hidden="true"
                  className="shrink-0"
                >
                  <path
                    d="M 0 5 L 36 5"
                    fill="none"
                    {...props}
                  />
                </svg>
                <span className="font-mono text-[10px] uppercase tracking-widest text-ink">
                  {entry.label}
                </span>
              </div>
              <p className="text-[11px] text-ink-2 leading-snug font-serif italic pl-1">
                {entry.meaning}
              </p>
            </div>
          );
        })}
      </div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-ink-3 leading-relaxed border-t border-hairline pt-3">
        Resolution is implementer responsibility. The protocol is a file format,
        a signature, and an anchor.
      </p>
    </div>
  );
}
